import React, { useEffect, useMemo, useState, useCallback } from 'react';
import objectPath from 'object-path';
import { connect } from 'react-redux';
// LayoutContext
import { useHtmlClassService } from '../_core/EnsLayout';
// Import Layout components
import Header from './header/Header';
import { HeaderMobile } from './header-mobile/HeaderMobile';
import { Aside } from './aside/Aside';
import Footer from './footer/Footer';
import { LayoutInit } from './LayoutInit';
import { SubHeader } from './subheader/SubHeader';
import openSocket from 'socket.io-client';
import { getAminChatIds } from '../../../app/modules/Admin/AdminActions';
import { bindActionCreators } from 'redux';
import * as setSettingData from '../../../redux/settingReducer';
import { APILINK } from '../../../config/config';
import QuickGuide from './QuickGuide/QuickGuide';
import { useLocation } from 'react-router-dom';
import { getQuickGuideText } from '../../../app/services/quickguide.service';

const WebSocketContext = React.createContext(null);

export { WebSocketContext };

function Layout({ children, auth, setSettingActions }) {
  const uiService = useHtmlClassService();
  const location = useLocation();

  // Layout settings (cssClasses/cssAttributes)
  const layoutProps = useMemo(() => {
    return {
      layoutConfig: uiService.config,
      selfLayout: objectPath.get(uiService.config, 'self.layout'),
      asideDisplay: objectPath.get(uiService.config, 'aside.self.display'),
      subheaderDisplay: objectPath.get(uiService.config, 'subheader.display'),
      desktopHeaderDisplay: objectPath.get(uiService.config, 'header.self.fixed.desktop'),
      contentCssClasses: uiService.getClasses('content', true),
      contentContainerClasses: uiService.getClasses('content_container', true),
      contentExtended: objectPath.get(uiService.config, 'content.extended')
    };
  }, [uiService]);

  const [socketObject, setSocketObject] = useState(null);
  const [showQuickGuide, setShowQuickGuide] = useState(false);
  const [guideText, setGuideText] = useState('');
  const currentPath = location.pathname;

  const connectSocket = async () => {
    console.log('🔗 Layout - Attempting to connect socket with token:', !!auth.authToken);
    try {
      const res = await openSocket(APILINK, {
        forceNew: false,
        transports: ['websocket'],
        query: {
          token: auth.authToken,
          webApp: true
        }
      });
      console.log('🔗 Layout - Socket connection result:', !!res, 'keys:', Object.keys(res || {}).length);
      if (Object.keys(res).length > 0) {
        console.log('✅ Layout - Setting socket object');
        setSocketObject(res);
      } else {
        console.log('❌ Layout - Socket connection failed, empty result');
      }
    } catch (e) {
      console.error('❌ Layout - Socket connection error:', e);
    }
  };

  const getSettingData = async () => {
    try {
      const res = await getAminChatIds();
      if (
        res.data &&
        Object.keys(res.data).length > 0 &&
        res.data.hasOwnProperty('chat_admin') &&
        res.data.chat_admin
      ) {
        const adminsIds = res.data.chat_admin.split(',').map((e) => parseInt(e));
        setSettingActions.setSettingData({ chat_admin: adminsIds });
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    console.log('🔗 Layout - useEffect triggered, auth.authToken:', !!auth.authToken);
    getSettingData().then();
    if (auth.authToken) {
      console.log('🔗 Layout - Auth token exists, connecting socket...');
      connectSocket().then((r) => {
        console.log('✅ Layout - SOCKET CONNECT COMPLETED');
      });
    } else {
      console.log('❌ Layout - No auth token, cannot connect socket');
    }
  }, [auth.authToken]);

  // Debug when socketObject changes
  useEffect(() => {
    console.log('🔗 Layout - socketObject changed:', !!socketObject);
  }, [socketObject]);

  const fetchData = useCallback((currentPath) => {
    getQuickGuideText({
      pageUrl: currentPath
    })
      .then((res) => {
        if (res.data && res.data.guideText) {
          setGuideText(res.data.guideText);
          setShowQuickGuide(true);
        }
      })
      .catch((error) => {});
  }, []);

  useEffect(() => {
    setGuideText('');
    setShowQuickGuide(false);
    fetchData(currentPath);
  }, [currentPath, fetchData]);

  const handleCloseClick = () => {
    setShowQuickGuide(false);
  };
  const handleQuickGuideToggle = () => {
    setShowQuickGuide((prev) => !prev);
  };

  return layoutProps.selfLayout !== 'blank' ? (
    <>
      <WebSocketContext.Provider value={socketObject}>
        {/*begin::Main*/}

        <HeaderMobile />
        <div className="d-flex flex-column flex-root">
          {/*begin::Page*/}
          <div className="d-flex flex-row flex-column-fluid page">
            {layoutProps.asideDisplay && <Aside />}
            {/*begin::Wrapper*/}
            <div
              className="d-flex flex-column flex-row-fluid wrapper"
              style={{ background: '#ffffff' }}
              id="kt_wrapper"
            >
              <Header />
              {/*begin::Content*/}
              {/* add class for two button in dashboard  */}
              <div
                id="kt_content"
                className={`content ${layoutProps.contentCssClasses} d-flex flex-column flex-column-fluid position-relative`}
              >
                {layoutProps.subheaderDisplay && (
                  <SubHeader
                    showQuickGuideIcon={!showQuickGuide && guideText !== ''}
                    onQuickGuideIconClick={handleQuickGuideToggle}
                  />
                )}
                {/*begin::Entry*/}

                {!layoutProps.contentExtended && (
                  <div className="d-flex flex-column-fluid">
                    {/*begin::Container*/}

                    <div className={layoutProps.contentContainerClasses}>
                      {showQuickGuide && (
                        <QuickGuide onCloseClick={handleCloseClick} guideText={guideText} />
                      )}
                      {children}
                    </div>
                    {/*end::Container*/}
                  </div>
                )}

                {layoutProps.contentExtended && { children }}
                {/*end::Entry*/}
              </div>
              {/*end::Content*/}
              <Footer />
            </div>
            {/*end::Wrapper*/}
          </div>
          {/*end::Page*/}
        </div>
        {/*end::Main*/}
      </WebSocketContext.Provider>
      <LayoutInit />
    </>
  ) : (
    // BLANK LAYOUT
    <div className="d-flex flex-column flex-root">{children}</div>
  );
}

const mapStateToProps = (state) => ({
  auth: state.auth
});
const mapDispatchToEvents = (dispatch) => {
  return {
    setSettingActions: bindActionCreators(setSettingData.actions, dispatch)
  };
};

export default connect(mapStateToProps, mapDispatchToEvents)(Layout);
